import { Redline } from 'redline-client-sdk';
import { getGAAvailableCookies } from '@libs/utils/helpers/getGoogleAnalyticsCookies';
import { getFbAvailableCookies } from '@libs/utils/helpers/getFacebookPixel';
import { RLExperienceEvents } from '../types';
import { pixel } from './pixel';
import { appsFlyerBridge } from './appsFlyer';

// Marketing identifiers (GA4 client_id + Meta fbp/fbc) read from cookies so the
// conversion can be rebuilt from Redline in the offline/batch pipeline.
// Fully defensive: never throws and only includes keys that are actually
// present — so the event payload is never polluted with undefined.
function getMarketingIds(): { ga?: string; fbc?: string; fbp?: string } {
  try {
    const { ga } = getGAAvailableCookies();
    const { fbc, fbp } = getFbAvailableCookies();
    return {
      ...(ga ? { ga } : {}),
      ...(fbc ? { fbc } : {}),
      ...(fbp ? { fbp } : {}),
    };
  } catch {
    return {};
  }
}

export const experienceEvents = (rl: Redline): RLExperienceEvents => ({
  redirectRequested: (payload = {}) => {
    const event = 'experience.RedirectRequested.v1';
    pixel(event, { event, ...payload });
    appsFlyerBridge().logEvent(event, { event, ...payload });
    return rl.track(event, { event, ...payload });
  },
  pageNotFound: (payload = {}) => {
    const event = 'experience.PageNotFound.v1';
    rl.track(event, payload);
    pixel(event, payload);
    appsFlyerBridge().logEvent(event, payload);
  },
  proposalStarted: (payload = {}) => {
    const event = 'experience.ProposalStarted.v1';
    // Attach GA4 (ga) + Meta (fbp/fbc) ids so the conversion can be rebuilt
    // from Redline in the offline/batch pipeline. Each is added only when
    // present — if none exist, `enriched` matches the original payload.
    const enriched = { ...payload, ...getMarketingIds() };
    pixel(event, enriched);
    appsFlyerBridge().logEvent(event, enriched);
    appsFlyerBridge().logEvent(`partner_ProposalStarted_v1`, {})
    return rl.track(event, enriched);
  },
  appCrashed: (payload = {}) => {
    const event = 'experience.AppCrashed.v1';
    return rl.track(event, payload);
  },
  appBackgrounded: (payload = {}) => {
    const event = 'experience.AppBackgrounded.v1';

    return rl.track(event, payload);
  },
  sessionRecorded: (payload = {}) => {
    const event = 'experience.SessionRecorded.v1';
    rl.track(event, payload);
    pixel(event, payload);
    appsFlyerBridge().logEvent(event, payload);
  },
  loadingStarted: (payload) => {
    const event = 'experience.LoadingStarted.v1';
    return rl.track(event, payload);
  },
  loadingCompleted: (payload) => {
    const event = 'experience.LoadingCompleted.v1';
    return rl.track(event, payload);
  },
  experienceDecided: (payload) => {
    const event = 'experience.ExperienceDecided.v1';
    return rl.track(event, payload);
  },
  requestRetried: (payload) => {
    const event = 'experience.RequestRetried.v1';
    appsFlyerBridge().logEvent(event, payload);
    return rl.track(event, payload);
  }
});
